/**
 * CDM Regulation Timers — Track 3.13.
 *
 * Live regulatory countdown for every open incident that triggers
 * DORA-ICT, NIS2, SEC cyber-disclosure (8-K Item 1.05) or PRA SS 1/21.
 * Watcher (Celery beat, every 5 min) auto-creates timers; admins mark
 * milestones reported here.
 *
 * Cards (NOT a table) — these are time-sensitive items that need rich
 * affordances: regulation badge, incident anchor, three-step stepper,
 * countdown to the next deadline, mark-reported buttons.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Scale, AlarmClock, Bell, ChevronLeft, RefreshCw, CheckCircle2,
  Clock, AlertTriangle, XCircle, Search, FileText,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface IncidentSummary {
  id: number
  title: string
  severity: string | null
  status: string | null
  started_at: string | null
  tags: string[]
  affects_critical_function: boolean
  is_material: boolean
}

interface Timer {
  id: string
  org_id: string
  incident_id: number
  regulation: string
  state: string
  initial_classification_at: string | null
  initial_report_deadline: string | null
  intermediate_report_deadline: string | null
  final_report_deadline: string | null
  initial_reported_at: string | null
  initial_reported_by: string | null
  intermediate_reported_at: string | null
  intermediate_reported_by: string | null
  final_reported_at: string | null
  final_reported_by: string | null
  notes: string | null
  next_milestone: string | null
  next_deadline: string | null
  seconds_until_deadline: number | null
  seconds_until_initial: number | null
  seconds_until_intermediate: number | null
  seconds_until_final: number | null
  overdue: boolean
  incident: IncidentSummary | null
}

interface TimerCounts {
  active: number
  overdue: number
  by_regulation: Record<string, number>
  by_jurisdiction: Record<string, number>
}

interface RegulationDef {
  code: string
  name: string
  jurisdiction: string
  initial_deadline_hours: number
  intermediate_deadline_hours: number | null
  final_deadline_hours: number | null
  initial_report_label: string
  intermediate_report_label: string | null
  final_report_label: string | null
  citation: string
}

const REG_TONE: Record<string, string> = {
  'DORA-ICT':              'bg-rose/15 text-rose-light border-rose/40',
  'NIS2':                  'bg-amber/15 text-amber-light border-amber/40',
  'SEC-CYBER-DISCLOSURE':  'bg-blue/15 text-blue-light border-blue/40',
  'PRA-SS-1-21':           'bg-purple/15 text-purple-light border-purple/40',
}

const STATE_TONE: Record<string, string> = {
  pending_initial:        'bg-amber/15 text-amber-light border-amber/30',
  initial_reported:       'bg-blue/15 text-blue-light border-blue/30',
  pending_intermediate:   'bg-amber/15 text-amber-light border-amber/30',
  intermediate_reported:  'bg-blue/15 text-blue-light border-blue/30',
  pending_final:          'bg-amber/15 text-amber-light border-amber/30',
  final_reported:         'bg-emerald/15 text-emerald border-emerald/30',
  cancelled:              'bg-text-muted/15 text-text-muted border-border',
}

const SEV_TONE: Record<string, string> = {
  sev1: 'text-rose-light',
  sev2: 'text-amber-light',
  sev3: 'text-cyan',
  sev4: 'text-text-muted',
}

function formatDuration(secs: number | null): string {
  if (secs === null || secs === undefined) return '—'
  const negative = secs < 0
  let n = Math.abs(secs)
  const days = Math.floor(n / 86400)
  n -= days * 86400
  const hours = Math.floor(n / 3600)
  n -= hours * 3600
  const minutes = Math.floor(n / 60)
  let out: string
  if (days > 0) out = `${days}d ${hours}h ${minutes}m`
  else if (hours > 0) out = `${hours}h ${minutes}m`
  else out = `${minutes}m`
  return negative ? `-${out}` : out
}

function MilestoneStep({
  label, deadline, reportedAt, reportedBy, isNext, secondsUntil, overdue,
  onMark, busy, canMark,
}: {
  label: string | null
  deadline: string | null
  reportedAt: string | null
  reportedBy: string | null
  isNext: boolean
  secondsUntil: number | null
  overdue: boolean
  onMark?: () => void
  busy: boolean
  canMark: boolean
}) {
  if (!label) {
    return (
      <div className="flex-1 flex items-center justify-center text-[10px] text-text-muted/50 italic px-2 py-2 border border-dashed border-border/50 rounded">
        —
      </div>
    )
  }
  const reported = !!reportedAt
  const tone =
    reported ? 'border-emerald/40 bg-emerald/5' :
    isNext && overdue ? 'border-rose/50 bg-rose/10 ring-2 ring-rose/30 animate-pulse' :
    isNext ? 'border-amber/50 bg-amber/5' :
    'border-border bg-surface-raised/40'
  return (
    <div className={clsx('flex-1 px-3 py-2 border rounded space-y-1', tone)}>
      <div className="flex items-center gap-1.5">
        {reported
          ? <CheckCircle2 size={11} className="text-emerald" />
          : isNext && overdue
            ? <Bell size={11} className="text-rose-light" />
            : <Clock size={11} className="text-text-muted" />}
        <span className="text-[10px] uppercase tracking-widest text-text-muted">{label}</span>
      </div>
      {reported ? (
        <>
          <div className="text-[10px] text-emerald font-mono">
            filed {reportedAt ? new Date(reportedAt).toLocaleString() : ''}
          </div>
          {reportedBy && (
            <div className="text-[10px] text-text-muted">by {reportedBy}</div>
          )}
        </>
      ) : (
        <>
          <div className="text-[10px] text-text-secondary font-mono">
            due {deadline ? new Date(deadline).toLocaleString() : '—'}
          </div>
          {isNext && (
            <div className={clsx('text-[11px] font-mono font-bold',
                                 overdue ? 'text-rose-light' : 'text-amber-light')}>
              {overdue ? 'OVERDUE ' : 'in '}{formatDuration(secondsUntil)}
            </div>
          )}
          {isNext && canMark && onMark && (
            <button type="button" onClick={onMark} disabled={busy}
                    className="btn-primary text-[10px] mt-1 w-full">
              Mark reported
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default function CdmRegulationTimersPage() {
  const [timers, setTimers] = useState<Timer[]>([])
  const [counts, setCounts] = useState<TimerCounts | null>(null)
  const [regulations, setRegulations] = useState<RegulationDef[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [regFilter, setRegFilter] = useState('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [search, setSearch] = useState('')

  const me = useMemo(() => {
    try {
      const raw = localStorage.getItem('user')
      return raw ? JSON.parse(raw) : null
    } catch { return null }
  }, [])
  const isAdmin = me?.role === 'admin'

  const load = () => {
    setLoading(true)
    const params = new URLSearchParams()
    if (stateFilter) params.set('state', stateFilter)
    if (regFilter) params.set('regulation', regFilter)
    if (overdueOnly) params.set('overdue', 'true')
    Promise.all([
      api.get('/api/cdm/regulation-timers?' + params.toString()),
      api.get('/api/cdm/regulations'),
    ])
      .then(([t, r]) => {
        setTimers(t.data?.timers || [])
        setCounts(t.data?.counts || null)
        setRegulations(r.data?.regulations || [])
      })
      .catch(e => setMessage(`Load failed: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [stateFilter, regFilter, overdueOnly]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tick once a minute so countdowns refresh
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000)
    return () => clearInterval(id)
  }, [])

  const markMilestone = async (timer: Timer, milestone: 'initial' | 'intermediate' | 'final') => {
    const notes = window.prompt(
      `Notes for the ${milestone} report (optional):`,
      '',
    )
    if (notes === null) return  // cancelled
    setBusy(`${timer.id}:${milestone}`)
    try {
      await api.post(`/api/cdm/regulation-timers/${timer.id}/report-${milestone}`,
                     { notes: notes.trim() || undefined })
      setMessage(`${timer.regulation}: ${milestone} milestone marked reported.`)
      load()
    } catch (e: any) {
      setMessage(`Failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  const cancelTimer = async (timer: Timer) => {
    const reason = window.prompt(
      `Reason for cancelling the ${timer.regulation} timer (optional):`, '',
    )
    if (reason === null) return
    setBusy(`${timer.id}:cancel`)
    try {
      await api.post(`/api/cdm/regulation-timers/${timer.id}/cancel`,
                     { reason: reason.trim() || undefined })
      setMessage(`${timer.regulation} timer cancelled.`)
      load()
    } catch (e: any) {
      setMessage(`Cancel failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  // Client-side search filter
  const visibleTimers = useMemo(() => {
    if (!search.trim()) return timers
    const q = search.toLowerCase().trim()
    return timers.filter(t =>
      t.regulation.toLowerCase().includes(q) ||
      (t.incident?.title || '').toLowerCase().includes(q) ||
      String(t.incident_id).includes(q),
    )
  }, [timers, search])

  const initialDueSoon = useMemo(() => {
    return timers.filter(t =>
      t.state === 'pending_initial' &&
      t.seconds_until_initial !== null &&
      t.seconds_until_initial >= 0 &&
      t.seconds_until_initial <= 4 * 3600,
    ).length
  }, [timers, tick])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <AlarmClock size={18} className="text-rose-light" /> CDM REGULATION TIMERS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          DORA-ICT · NIS2 · SEC 8-K · PRA SS 1/21
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {/* Hero tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-4">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Active timers</div>
          <div className="text-3xl font-black font-mono mt-1 text-text-primary">
            {counts?.active ?? 0}
          </div>
          <div className="text-[10px] text-text-muted mt-1">across all regulations</div>
        </div>
        <div className="card p-4">
          <div className="text-[10px] uppercase tracking-widest text-text-muted flex items-center gap-1">
            <Bell size={10} /> Overdue
          </div>
          <div className={clsx('text-3xl font-black font-mono mt-1',
                               (counts?.overdue ?? 0) > 0 ? 'text-rose-light' : 'text-emerald')}>
            {counts?.overdue ?? 0}
          </div>
          <div className="text-[10px] text-text-muted mt-1">past their next deadline</div>
        </div>
        <div className="card p-4">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Initial due ≤ 4h</div>
          <div className={clsx('text-3xl font-black font-mono mt-1',
                               initialDueSoon > 0 ? 'text-amber-light' : 'text-text-primary')}>
            {initialDueSoon}
          </div>
          <div className="text-[10px] text-text-muted mt-1">DORA initial classification window</div>
        </div>
        <div className="card p-4">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">By jurisdiction</div>
          <div className="space-y-1 mt-2">
            {(['EU', 'UK', 'US', 'Global'] as const).map(j => {
              const n = counts?.by_jurisdiction[j] ?? 0
              const total = counts ? Math.max(1, Object.values(counts.by_jurisdiction).reduce((a, b) => a + b, 0)) : 1
              const pct = Math.round((n / total) * 100)
              return (
                <div key={j} className="flex items-center gap-2">
                  <span className="text-[10px] text-text-muted w-10">{j}</span>
                  <div className="flex-1 h-2 bg-surface-raised rounded overflow-hidden">
                    <div className="h-full bg-cyan" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-text-secondary w-6 text-right">{n}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-widest text-text-muted">Filters</span>
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                className="bg-surface-raised text-xs border border-border rounded px-2 py-1">
          <option value="">All states</option>
          <option value="pending_initial">Pending initial</option>
          <option value="initial_reported">Initial reported</option>
          <option value="pending_intermediate">Pending intermediate</option>
          <option value="intermediate_reported">Intermediate reported</option>
          <option value="pending_final">Pending final</option>
          <option value="final_reported">Final reported</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={regFilter} onChange={e => setRegFilter(e.target.value)}
                className="bg-surface-raised text-xs border border-border rounded px-2 py-1">
          <option value="">All regulations</option>
          {regulations.map(r => (
            <option key={r.code} value={r.code}>{r.code} ({r.jurisdiction})</option>
          ))}
        </select>
        <label className="text-[11px] text-text-secondary inline-flex items-center gap-1.5">
          <input type="checkbox" checked={overdueOnly}
                 onChange={e => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>
        <div className="flex items-center gap-1 border border-border rounded px-2 py-1 bg-surface-raised">
          <Search size={11} className="text-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="search incident / regulation"
                 className="bg-transparent text-xs outline-none w-56" />
        </div>
      </div>

      {/* Timer cards */}
      {loading && timers.length === 0 && (
        <div className="card p-6 text-center text-text-muted text-sm">Loading timers…</div>
      )}
      {!loading && visibleTimers.length === 0 && (
        <div className="card p-6 text-center text-text-muted text-sm space-y-2">
          <Scale size={28} className="mx-auto opacity-50" />
          <div>No regulation timers match the current filter.</div>
          <div className="text-[11px]">
            New incidents tagged <code className="font-mono">affects_critical_function</code>
            {' '}will auto-start timers within 5 min via the
            <code className="font-mono"> cdm-regulation-timer-watcher</code> beat task.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {visibleTimers.map(t => {
          const reg = regulations.find(r => r.code === t.regulation)
          const initialReported = !!t.initial_reported_at
          const intermediateReported = !!t.intermediate_reported_at
          const finalReported = !!t.final_reported_at
          const cancelled = t.state === 'cancelled'

          return (
            <div key={t.id}
                 className={clsx('card p-4 space-y-3',
                                 t.overdue && !cancelled && 'ring-1 ring-rose/40')}>
              {/* Header row */}
              <div className="flex items-start gap-3 flex-wrap">
                <span className={clsx('px-2.5 py-1 rounded border text-[11px] font-bold uppercase tracking-wider',
                                      REG_TONE[t.regulation] || 'bg-text-muted/15 text-text-muted border-border')}>
                  {t.regulation}
                </span>
                {reg && (
                  <span className="text-[10px] text-text-muted self-center">
                    {reg.jurisdiction} · {reg.citation}
                  </span>
                )}
                <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider self-center',
                                      STATE_TONE[t.state] || 'bg-text-muted/15 text-text-muted border-border')}>
                  {t.state.replace(/_/g, ' ')}
                </span>
                {t.overdue && (
                  <span className="px-2 py-0.5 rounded border bg-rose/15 text-rose-light border-rose/40 text-[10px] uppercase tracking-wider self-center inline-flex items-center gap-1">
                    <Bell size={10} className="animate-pulse" /> Overdue
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {isAdmin && !cancelled && t.state !== 'final_reported' && (
                    <button type="button" onClick={() => cancelTimer(t)}
                            disabled={busy === `${t.id}:cancel`}
                            className="btn-ghost text-[10px]">
                      <XCircle size={11} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Incident row */}
              <div className="border-t border-border pt-3">
                {t.incident ? (
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-[10px] uppercase tracking-wider font-mono',
                                              SEV_TONE[t.incident.severity || ''] || 'text-text-muted')}>
                          {t.incident.severity?.toUpperCase() || 'SEV?'}
                        </span>
                        <Link to={`/app/postmortems`}
                              className="text-text-primary text-sm font-medium hover:text-blue-light">
                          {t.incident.title}
                        </Link>
                      </div>
                      <div className="text-[10px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>incident #{t.incident.id}</span>
                        <span>·</span>
                        <span>opened {t.incident.started_at ? new Date(t.incident.started_at).toLocaleString() : '—'}</span>
                        {t.incident.affects_critical_function && (
                          <>
                            <span>·</span>
                            <span className="text-amber-light inline-flex items-center gap-1">
                              <AlertTriangle size={10} /> critical function
                            </span>
                          </>
                        )}
                        {t.incident.is_material && (
                          <>
                            <span>·</span>
                            <span className="text-rose-light">SEC-material</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-text-muted italic">
                    incident #{t.incident_id} (record not found)
                  </div>
                )}
              </div>

              {/* Three-step stepper */}
              <div className="flex items-stretch gap-2">
                <MilestoneStep
                  label={reg?.initial_report_label || 'Initial'}
                  deadline={t.initial_report_deadline}
                  reportedAt={t.initial_reported_at}
                  reportedBy={t.initial_reported_by}
                  isNext={t.next_milestone === 'initial' && !cancelled}
                  secondsUntil={t.seconds_until_initial}
                  overdue={t.overdue && t.next_milestone === 'initial'}
                  onMark={() => markMilestone(t, 'initial')}
                  busy={busy === `${t.id}:initial`}
                  canMark={isAdmin && !cancelled}
                />
                <MilestoneStep
                  label={reg?.intermediate_report_label || null}
                  deadline={t.intermediate_report_deadline}
                  reportedAt={t.intermediate_reported_at}
                  reportedBy={t.intermediate_reported_by}
                  isNext={t.next_milestone === 'intermediate' && !cancelled}
                  secondsUntil={t.seconds_until_intermediate}
                  overdue={t.overdue && t.next_milestone === 'intermediate'}
                  onMark={() => markMilestone(t, 'intermediate')}
                  busy={busy === `${t.id}:intermediate`}
                  canMark={isAdmin && !cancelled}
                />
                <MilestoneStep
                  label={reg?.final_report_label || null}
                  deadline={t.final_report_deadline}
                  reportedAt={t.final_reported_at}
                  reportedBy={t.final_reported_by}
                  isNext={t.next_milestone === 'final' && !cancelled}
                  secondsUntil={t.seconds_until_final}
                  overdue={t.overdue && t.next_milestone === 'final'}
                  onMark={() => markMilestone(t, 'final')}
                  busy={busy === `${t.id}:final`}
                  canMark={isAdmin && !cancelled}
                />
              </div>

              {/* Notes */}
              {t.notes && (
                <details className="text-[11px] text-text-secondary border-t border-border pt-2">
                  <summary className="cursor-pointer inline-flex items-center gap-1 text-text-muted">
                    <FileText size={11} /> Notes
                  </summary>
                  <pre className="whitespace-pre-wrap mt-1 font-mono text-[10px] bg-surface-raised/40 p-2 rounded">
                    {t.notes}
                  </pre>
                </details>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
